import { Fragment, useEffect } from 'react'
import { Dialog, DialogBackdrop, DialogPanel, Transition } from '@headlessui/react'
import { FC, c } from 'lib/component-utils'
import ScrollArea from './ScrollArea'

type Props = {
  open: boolean
  setOpen: (value: boolean) => void
  disableBodyScrollLock?: boolean
}

const Modal: FC<Props> = ({
  open,
  setOpen,
  disableBodyScrollLock = false,
  children,
  className,
  ...rest
}) => {
  useEffect(() => {
    if (disableBodyScrollLock) {
      return
    }

    if (open) {
      document.body.style.overflow = 'hidden'
      return
    }
    document.body.style.overflow = ''
  }, [open, disableBodyScrollLock])

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        static={true}
        className="fixed inset-0 z-[1001] flex items-center justify-center p-6"
        open={open}
        onClose={() => setOpen(false)}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <DialogBackdrop className="fixed inset-0 transition-opacity bg-gray-500/75 dark:bg-white/80" />
        </Transition.Child>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          enterTo="opacity-100 translate-y-0 sm:scale-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100 translate-y-0 sm:scale-100"
          leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
        >
          <DialogPanel
            className={c`relative max-w-full max-h-full transition-all transform bg-white rounded-lg shadow-xl dark:ring-1 dark:ring-black/5 ${className}`}
            {...rest}
          >
            <ScrollArea vertical type="scroll" maxHeightClass="max-h-[calc(100vh-3rem)]">
              {children}
            </ScrollArea>
          </DialogPanel>
        </Transition.Child>
      </Dialog>
    </Transition.Root>
  )
}

export default Modal
